/* @jsx h */
import { defineComponent, h, PropType } from "vue";
import { Order } from "@lemmonhq/types";
import { useLocalization } from "@lemmonhq/uikit";
import { formatCurrency, formatDate } from "../../utils";
import { formatOrderId, orderStatusLabel } from "./customerDetailHelpers";
import { useAuth } from "@app/vue/providers/Auth";

export const CustomerCRMOrdersSection = defineComponent({
  name: "CustomerCRMOrdersSection",
  props: {
    orders: { type: Array as PropType<Order[]>, required: true },
    loading: { type: Boolean, required: true },
  },
  setup(props) {
    const { t } = useLocalization();
    const auth = useAuth();

    return () => (
      <div class="max-w-4xl">
        {props.loading ? (
          <div class="space-y-2">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                class="rounded-xl bg-base-100/30 p-4 animate-pulse motion-reduce:animate-none"
              >
                <div class="flex items-center gap-3">
                  <div class="w-8 h-8 rounded-lg bg-base-content/8" />
                  <div class="flex-1 space-y-1.5">
                    <div class="h-3 w-24 bg-base-content/8 rounded" />
                    <div class="h-2.5 w-32 bg-base-content/5 rounded" />
                  </div>
                  <div class="h-4 w-16 bg-base-content/8 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : props.orders.length > 0 ? (
          <div class="space-y-1.5">
            {props.orders.map((order: any) => (
              <div
                key={order.id}
                class="bg-base-100/40 rounded-xl px-4 py-3 border border-base-content/5 flex items-center gap-3 hover:bg-base-100/60 transition-colors duration-75"
              >
                <div class="w-9 h-9 rounded-lg bg-info/8 flex items-center justify-center flex-shrink-0">
                  <span class="material-symbols-outlined text-[16px] text-info">
                    receipt_long
                  </span>
                </div>
                <div class="flex-1 min-w-0">
                  <div class="text-sm font-medium text-accent">
                    {t("reservations.customers.order_number", [
                      formatOrderId(order.id),
                    ])}
                  </div>
                  <div class="text-[11px] text-base-content/35 flex items-center gap-2">
                    <span>
                      {formatDate(order.created_timestamp || order.created_at)}
                    </span>
                    {order.service_method && (
                      <>
                        <span class="text-base-content/15">·</span>
                        <span>{order.service_method}</span>
                      </>
                    )}
                  </div>
                </div>
                <div class="text-right flex-shrink-0">
                  <div class="text-sm font-semibold text-success tabular-nums">
                    {formatCurrency(
                      order.amount || order.total,
                      auth.user.value?.restaurant.currency || "CHF",
                    )}
                  </div>
                  {order.status && (
                    <span
                      class={`badge badge-xs ${order.status === "completed" ? "badge-success badge-outline" : order.status === "cancelled" ? "badge-error badge-outline" : "badge-ghost"}`}
                    >
                      {orderStatusLabel(order.status, t)}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div class="flex items-center justify-center h-48">
            <div class="text-center space-y-2">
              <span class="material-symbols-outlined text-3xl text-base-content/15">
                receipt_long
              </span>
              <p class="text-sm text-base-content/30">
                {t("reservations.customers.no_orders_found")}
              </p>
            </div>
          </div>
        )}
      </div>
    );
  },
});
